import React, { useState, useMemo } from 'react'
import { Typography, Popover, Box, IconButton } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'

import { type DraftCard } from '../../store/bulk-import.store.types'

import StandardSelectionDropdown from '@/components/StandardSelectionDropdown'
import TargetProficiencyLevelSelectionDropdown from '@/components/TargetProficiencyLevelSelectionDropdown'
import { useProficiencyStandards, type ProficiencyStandard } from '@/hooks/useProficiencyStandards'

interface StandardsBasedCriteriaEditorProps {
  card: DraftCard
  updateCard: (cardId: string, field: keyof DraftCard, value: any) => void
}

export function StandardsBasedCriteriaEditor({ card, updateCard }: StandardsBasedCriteriaEditorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const {
    data: standardsData,
    isLoading: isLoadingStandards,
    error: errorStandards,
  } = useProficiencyStandards({ language: card.language || 'en' })

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? `standards-editor-popover-${card.id}` : undefined

  const selectedStandard = useMemo<ProficiencyStandard | undefined>(() => {
    return standardsData?.find(s => s.id === card.standardId)
  }, [standardsData, card.standardId])

  const handleStandardChange = (newStandardId: string) => {
    updateCard(card.id, 'standardId', newStandardId)
    // get first level of the new standard
    const firstLevel = standardsData?.find(s => s.id === newStandardId)?.levels[0]

    if (firstLevel) {
      setTimeout(() => {
        updateCard(card.id, 'target_proficiency_level', firstLevel?.id)
      }, 500)
    }
  }

  const handleTargetLevelChange = (newTargetLevel: string) => {
    updateCard(card.id, 'target_proficiency_level', newTargetLevel)
  }

  const displayStandard = card.standardId?.toUpperCase() || 'N/A'
  const displayLevel = card.target_proficiency_level || 'N/A'

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleClick as any}>
        <Box border="1px solid #D0D5DD" borderRadius="6px" p="2px 6px">
          <Typography fontSize="12px" fontWeight="500">
            {displayStandard} - {displayLevel}
          </Typography>
        </Box>
      </Box>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        id={id}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={e => {
          e.stopPropagation()
        }}
        onClose={handleClose}
      >
        <Box
          sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <Typography variant="subtitle2">Edit Standards Based Criteria</Typography>
          <StandardSelectionDropdown
            disabled={isLoadingStandards || !!errorStandards}
            error={!!errorStandards}
            helperText={errorStandards ? 'Error loading standards' : undefined}
            label="Proficiency Standard"
            loading={isLoadingStandards}
            selectedStandardId={card.standardId || ''}
            standards={standardsData || []}
            onChange={handleStandardChange}
          />
          <TargetProficiencyLevelSelectionDropdown
            disabled={!selectedStandard || isLoadingStandards}
            label="Target Proficiency Level"
            levels={selectedStandard?.levels || []}
            selectedLevelId={card.target_proficiency_level || ''}
            standardName={selectedStandard?.name}
            onChange={handleTargetLevelChange}
          />
        </Box>
      </Popover>
    </>
  )
}
